/**
 * compliance_gaps — safe agent tool that returns the 4-way gap matrix
 * for a folder + framework.
 */

import type { IAgentTool, IAgentToolContext, AgentToolResult } from '../../../../core/ai/agent/IAgentTool';

interface GapRow {
  controlId: string;
  controlLabel: string;
  category: 'documented_no_evidence' | 'undocumented_with_evidence' | 'partially_met' | 'fully_met';
  processNodeCount: number;
  evidenceNodeCount: number;
  reason: string;
}

interface GapResponse {
  framework: string;
  graphId: string;
  evaluatedAt: string;
  summary: {
    total: number;
    documented_no_evidence: number;
    undocumented_with_evidence: number;
    partially_met: number;
    fully_met: number;
  };
  gaps: GapRow[];
}

function getFolderId(context: IAgentToolContext): string | null {
  const ctx = context.assistantContext as Record<string, unknown> | undefined;
  const f = ctx?.folderId;
  return typeof f === 'string' && f.length > 0 ? f : null;
}

export const complianceGapsTool: IAgentTool = {
  name: 'compliance_gaps',
  description:
    'Get the 4-way compliance gap matrix (documented_no_evidence / undocumented_with_evidence / partially_met / fully_met) for a framework in the current folder. Use when user asks about gaps, missing evidence, or where coverage is weak.',
  parameters: {
    type: 'object',
    properties: {
      framework: { type: 'string', description: 'Framework id (default soc2)' },
      category: {
        type: 'string',
        description: 'Optional. Filter gaps to a single category.',
        enum: ['documented_no_evidence', 'undocumented_with_evidence', 'partially_met', 'fully_met'],
      },
    },
  },
  supportedDiagramTypes: [],
  tier: 'safe',
  category: 'query',

  async execute(params, context): Promise<AgentToolResult> {
    const folderId = getFolderId(context);
    if (!folderId) return { success: false, summary: 'No folder context available.' };
    const framework = typeof params.framework === 'string' && params.framework ? params.framework : 'soc2';
    const filterCategory = typeof params.category === 'string' ? params.category : null;

    const url = `/api/compass/folders/${encodeURIComponent(folderId)}/compliance/${encodeURIComponent(framework)}/gaps`;
    const response = await fetch(url);
    if (!response.ok) return { success: false, summary: `Gap matrix request failed (${response.status})` };
    const body = (await response.json()) as GapResponse;
    const filtered = filterCategory
      ? body.gaps.filter((g) => g.category === filterCategory)
      : body.gaps;
    const lines = filtered
      .slice(0, 12)
      .map((g) => `- ${g.controlId} [${g.category}]: ${g.reason}`)
      .join('\n');

    const s = body.summary;
    return {
      success: true,
      summary:
        `${framework} gaps — total: ${s.total}. ` +
        `documented_no_evidence: ${s.documented_no_evidence}, ` +
        `undocumented_with_evidence: ${s.undocumented_with_evidence}, ` +
        `partially_met: ${s.partially_met}, ` +
        `fully_met: ${s.fully_met}.` +
        (lines ? `\n\n${lines}` : ''),
      data: { gaps: filtered },
      uiSurface: 'chat',
    };
  },
};
